import { normalizeCatalogueName } from "./dtoolsProjectCatalogKey";

export type CatalogMatchConfidence = "matched" | "possible" | "none";

export type DtoolsLineCatalogMeta = {
  catalogProductId?: number | null;
  projectCatalogueId?: number | null;
  catalogMatchConfidence?: CatalogMatchConfidence | null;
  proposalImageOverride?: string | null;
  proposalDescriptionOverride?: string | null;
};

export type RetailCatalogueMatchCandidate = {
  id: number;
  brand: string;
  model: string;
  name: string;
  sku?: string | null;
  brandNorm?: string | null;
  modelNorm?: string | null;
  skuNorm?: string | null;
  imageUrl?: string | null;
  description?: string | null;
};

export type RetailCatalogueLineInput = {
  manufacturer?: string | null;
  model?: string | null;
  sku?: string | null;
  displayName?: string | null;
  catalogId?: string | null;
};

export function normaliseCatalogueLookupKey(value: string | null | undefined): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function normaliseCatalogueSku(value: string | null | undefined): string | null {
  const cleaned = String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9\-_]/g, "");
  return cleaned || null;
}

export type ScoredCatalogueMatch = {
  candidate: RetailCatalogueMatchCandidate;
  confidence: CatalogMatchConfidence;
  score: number;
  reason: string;
};

export function scoreRetailCatalogueMatch(
  input: RetailCatalogueLineInput,
  candidate: RetailCatalogueMatchCandidate,
): ScoredCatalogueMatch | null {
  const brandNorm = normaliseCatalogueLookupKey(input.manufacturer ?? candidate.brand);
  const modelNorm = normaliseCatalogueLookupKey(input.model ?? candidate.model);
  const skuNorm = normaliseCatalogueSku(input.sku ?? input.catalogId ?? candidate.sku);
  const nameNorm = normalizeCatalogueName(
    input.displayName ??
      ([input.manufacturer, input.model].filter(Boolean).join(" ") || candidate.name),
  );

  const cBrand = normaliseCatalogueLookupKey(candidate.brandNorm ?? candidate.brand);
  const cModel = normaliseCatalogueLookupKey(candidate.modelNorm ?? candidate.model);
  const cSku = normaliseCatalogueSku(candidate.skuNorm ?? candidate.sku);
  const cName = normalizeCatalogueName(candidate.name);

  if (skuNorm && cSku && skuNorm === cSku) {
    return { candidate, confidence: "matched", score: 100, reason: "sku" };
  }

  if (brandNorm && modelNorm && cBrand && cModel && brandNorm === cBrand && modelNorm === cModel) {
    return { candidate, confidence: "matched", score: 95, reason: "brand+model" };
  }

  if (brandNorm && cBrand && brandNorm === cBrand && modelNorm && cModel) {
    if (cModel.includes(modelNorm) || modelNorm.includes(cModel)) {
      return { candidate, confidence: "possible", score: 72, reason: "brand+partial-model" };
    }
  }

  if (nameNorm && cName) {
    if (nameNorm === cName) {
      return { candidate, confidence: "possible", score: 68, reason: "normalized-name" };
    }
    if (nameNorm.length >= 8 && (cName.includes(nameNorm) || nameNorm.includes(cName))) {
      return { candidate, confidence: "possible", score: 55, reason: "name-substring" };
    }
  }

  return null;
}

export function pickBestRetailCatalogueMatch(
  input: RetailCatalogueLineInput,
  candidates: RetailCatalogueMatchCandidate[],
): ScoredCatalogueMatch | null {
  let best: ScoredCatalogueMatch | null = null;
  for (const candidate of candidates) {
    const scored = scoreRetailCatalogueMatch(input, candidate);
    if (!scored) continue;
    if (!best || scored.score > best.score) best = scored;
    else if (scored.score === best.score && scored.confidence === "matched" && best.confidence !== "matched") {
      best = scored;
    }
  }
  return best;
}

/**
 * Effective catalogue match for badges and staff UI.
 * An authoritative catalogue ID always wins over a missing confidence stamp.
 * Images / descriptions alone never imply a match.
 */
export function resolveEffectiveCatalogMatchConfidence(input: {
  catalogMatchConfidence?: CatalogMatchConfidence | string | null;
  catalogProductId?: number | null;
  projectCatalogueId?: number | null;
}): CatalogMatchConfidence {
  const projectId = Number(input.projectCatalogueId ?? 0);
  if (Number.isFinite(projectId) && projectId > 0) return "matched";
  const productId = Number(input.catalogProductId ?? 0);
  if (Number.isFinite(productId) && productId > 0) return "matched";
  const stored = input.catalogMatchConfidence;
  if (stored === "matched" || stored === "possible" || stored === "none") return stored;
  return "none";
}

export function catalogMatchStatusLabel(confidence: CatalogMatchConfidence | null | undefined): string {
  if (confidence === "matched") return "Matched from catalogue";
  if (confidence === "possible") return "Possible match";
  return "No catalogue match";
}

export function catalogMatchStatusEmoji(confidence: CatalogMatchConfidence | null | undefined): string {
  if (confidence === "matched") return "✅";
  if (confidence === "possible") return "⚠️";
  return "❌";
}

export function parseDtoolsLineCatalogMeta(raw: unknown): DtoolsLineCatalogMeta {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const r = raw as Record<string, unknown>;
  const confidence = r.catalogMatchConfidence;
  const validConfidence =
    confidence === "matched" || confidence === "possible" || confidence === "none" ? confidence : null;
  const catalogProductId = Number(r.catalogProductId);
  const projectCatalogueId = Number(r.projectCatalogueId);
  const productId = Number.isFinite(catalogProductId) && catalogProductId > 0 ? catalogProductId : null;
  const projectId =
    Number.isFinite(projectCatalogueId) && projectCatalogueId > 0 ? projectCatalogueId : null;
  const linked = productId != null || projectId != null;
  return {
    catalogProductId: productId,
    projectCatalogueId: projectId,
    // Historical linked rows often omit catalogMatchConfidence — treat authoritative IDs as matched.
    catalogMatchConfidence: linked
      ? resolveEffectiveCatalogMatchConfidence({
          catalogMatchConfidence: validConfidence,
          catalogProductId: productId,
          projectCatalogueId: projectId,
        })
      : validConfidence,
    proposalImageOverride:
      typeof r.proposalImageOverride === "string" ? r.proposalImageOverride.trim() || null : null,
    proposalDescriptionOverride:
      typeof r.proposalDescriptionOverride === "string" ? r.proposalDescriptionOverride.trim() || null : null,
  };
}

export function mergeDtoolsLineCatalogMeta(
  base: Record<string, unknown>,
  patch: DtoolsLineCatalogMeta,
): Record<string, unknown> {
  const next = { ...base };
  if (patch.catalogProductId !== undefined) next.catalogProductId = patch.catalogProductId;
  if (patch.projectCatalogueId !== undefined) next.projectCatalogueId = patch.projectCatalogueId;
  if (patch.catalogMatchConfidence !== undefined) next.catalogMatchConfidence = patch.catalogMatchConfidence;
  if (patch.proposalImageOverride !== undefined) next.proposalImageOverride = patch.proposalImageOverride;
  if (patch.proposalDescriptionOverride !== undefined) {
    next.proposalDescriptionOverride = patch.proposalDescriptionOverride;
  }
  return next;
}

export function resolveLineDisplayImage(
  lineImageUrl: string | null | undefined,
  meta: DtoolsLineCatalogMeta,
  catalogueImageUrl?: string | null,
): string | null {
  if (meta.proposalImageOverride?.trim()) return meta.proposalImageOverride.trim();
  if (lineImageUrl?.trim()) return lineImageUrl.trim();
  if (catalogueImageUrl?.trim()) return catalogueImageUrl.trim();
  return null;
}

export function resolveLineDisplayDescription(
  lineDescription: string | null | undefined,
  meta: DtoolsLineCatalogMeta,
  catalogueDescription?: string | null,
): string {
  if (meta.proposalDescriptionOverride?.trim()) return meta.proposalDescriptionOverride.trim();
  if (lineDescription?.trim()) return lineDescription.trim();
  if (catalogueDescription?.trim()) return catalogueDescription.trim();
  return "";
}

/**
 * Customer-facing visible copy for a D-Tools staging line (Step 2 → Step 3 / Link A/B).
 *
 * Priority (proposal-level override, then intelligent fallback):
 * 1. proposalDescriptionOverride
 * 2. polishedDescription / descriptionSnapshot
 * 3. cleoDescription
 * 4. original D-Tools / imported description
 *
 * Never deletes or overwrites cleoDescription — this is display selection only.
 */
export function resolveStagingLineVisibleDescription(line: {
  cleoDescription?: string | null;
  polishedDescription?: string | null;
  dtoolsDescription?: string | null;
  proposalDescriptionOverride?: string | null;
}): string | null {
  const meta = parseDtoolsLineCatalogMeta(line);
  const proposalSpecific = resolveLineDisplayDescription(line.polishedDescription, meta);
  if (proposalSpecific) return proposalSpecific;

  const cleo = String(line.cleoDescription ?? "").trim();
  if (cleo) return cleo;

  const dtools = String(line.dtoolsDescription ?? "").trim();
  return dtools || null;
}
